import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Field,
  FieldGroup,
  Input,
  Select
} from '@itds/elements/index.js';

import {
  GUARANTEED_UNIVERSAL_LIFE_PRODUCTS,
  OTHER_PRODUCTS,
  ROP_PRODUCTS,
  TERM_LIFE_INSURANCE_PRODUCTS,
  PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE
} from '@itds/Constants';

import Api from '@carrier/utils/Api';

const Edit = ({ band, onClose, onSave, show }) => {
  const [data, setData] = useState({
    id: null,
    carrier_id: null,
    data_version_id: null,
    band_name: '',
    product_type: '',
    face_amount_start: '',
    face_amount_end: '',
    age_start: '',
    age_end: '',
    min_premium_annual: '',
    max_premium_annual: '',
    min_premium_semi_annual: '',
    max_premium_semi_annual: '',
    min_premium_quarterly: '',
    max_premium_quarterly: '',
    min_premium_monthly: '',
    max_premium_monthly: '',
    policy_fee_annual: '',
    policy_fee_monthly: '',
    policy_fee_quarterly: '',
    policy_fee_semi_annual: '',
    disable_flat_extra: '',
    age_calculation_type: ''
  });
  const [errors, setErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);

  useEffect(() => {
    const new_band = band || {};

    setData({
      id: new_band.id,
      carrier_id: new_band.carrier_id,
      data_version_id: new_band.data_version_id,
      band_name: new_band.band_name || '',
      product_type: new_band.product_type || '',
      face_amount_start: `${new_band.face_amount_start || 0}`,
      face_amount_end: `${new_band.face_amount_end || 0}`,
      age_start: `${new_band.age_start || 0}`,
      age_end: `${new_band.age_end || 0}`,
      min_premium_annual: new_band.min_premium_annual || '',
      max_premium_annual: new_band.max_premium_annual || '',
      min_premium_semi_annual: new_band.min_premium_semi_annual || '',
      max_premium_semi_annual: new_band.max_premium_semi_annual || '',
      min_premium_quarterly: new_band.min_premium_quarterly || '',
      max_premium_quarterly: new_band.max_premium_quarterly || '',
      min_premium_monthly: new_band.min_premium_monthly || '',
      max_premium_monthly: new_band.max_premium_monthly || '',
      policy_fee_annual: new_band.policy_fee_annual || '',
      policy_fee_monthly: new_band.policy_fee_monthly || '',
      policy_fee_quarterly: new_band.policy_fee_quarterly || '',
      policy_fee_semi_annual: new_band.policy_fee_semi_annual || '',
      disable_flat_extra: new_band.disable_flat_extra || '',
      age_calculation_type: new_band.age_calculation_type || 'age_nearest'
    });
  }, [band]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheckboxChange = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };
  const handleComma = data => {
    const result = {};

    for (const key in data) {
      if (typeof data[key] === 'string') {
        const numericValue = Number(data[key].replaceAll(',', ''));

        if (!isNaN(numericValue)) {
          result[key] = numericValue;
        } else {
          result[key] = data[key];
        }
      } else {
        result[key] = data[key];
      }
    }

    return result;
  };
  const handleSave = () => {
    const errors = [];
    const required = ['face_amount_start', 'face_amount_end', 'age_start', 'age_end'];

    if (!data.band_name) {
      errors.push('band_name');
    }

    required.forEach(field => {
      if (!data[field].length) {
        errors.push(field);
      }
    });

    setErrors(errors);

    if (!errors.length) {
      const updatedData = handleComma(data);
      const { id, ...payload } = updatedData;

      if (!payload.product_type || payload.product_type === '') {
        payload.product_type = 'N/A';
      }
      Api.saveBand(id, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };
  const getProductTypeOptions = () => {
    const options = [];

    // Term Life Insurance Products
    options.push({
      value: 'term_life_header',
      label: '--- Term Life Insurance ---',
      disabled: true
    });
    TERM_LIFE_INSURANCE_PRODUCTS.forEach(product => {
      options.push({ value: product, label: product });
    });

    // Guaranteed Universal Life Products
    options.push({
      value: 'gul_header',
      label: '--- Guaranteed Universal Life ---',
      disabled: true
    });
    GUARANTEED_UNIVERSAL_LIFE_PRODUCTS.forEach(product => {
      options.push({ value: product, label: product });
    });
    // Variable Universal Life Products
    options.push({
      value: 'vul',
      label: '--- Variable Universal Life ---',
      disabled: true
    });
    PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE?.VUL?.forEach(product => {
      options.push({ value: product, label: product });
    });
    // Indexed Universal Life Products
    options.push({
      value: 'iul',
      label: '--- Indexed Universal Life ---',
      disabled: true
    });
    PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE?.IUL?.forEach(product => {
      options.push({ value: product, label: product });
    });
    // ROP Products
    options.push({ value: 'rop_header', label: '--- Return of Premium ---', disabled: true });
    ROP_PRODUCTS.forEach(product => {
      options.push({ value: product, label: product });
    });

    // Other Products
    options.push({ value: 'other_header', label: '--- Other Products ---', disabled: true });
    OTHER_PRODUCTS.forEach(product => {
      options.push({ value: product, label: product });
    });

    return options;
  };
  return (
    <Drawer
      alignRight={true}
      footer={<Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create Band'}</Button>}
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Band' : 'New Band'}
    >
      {save_error ? (
        <Alert type='error'>An error occurred while trying to save the band.</Alert>
      ) : null}

      <Field label='Name'>
        <Input
          invalid={errors.includes('band_name')}
          onChange={handleInputChange.bind(null, 'band_name')}
          required={true}
          value={data.band_name}
        />
      </Field>

      <Field label='Age Calculation Type'>
        <Select
          onChange={handleInputChange.bind(null, 'age_calculation_type')}
          options={[
            { value: 'age_nearest', label: 'Age Nearest' },
            { value: 'actual_age', label: 'Actual Age (Current Age)' }
          ]}
          required={true}
          value={data.age_calculation_type}
        />
      </Field>

      <Field label='Product Type'>
        <Select
          onChange={handleInputChange.bind(null, 'product_type')}
          options={[{ value: '', label: 'Select Product Type' }, ...getProductTypeOptions()]}
          value={data.product_type}
        />
      </Field>

      <div style={{ display: 'flex' }}>
        <Field label='Face Amount Start' style={{ flex: 1 }}>
          <Input
            format='currency'
            invalid={errors.includes('face_amount_start')}
            onChange={handleInputChange.bind(null, 'face_amount_start')}
            required={true}
            value={data.face_amount_start}
          />
        </Field>
        <Field label='Face Amount End' style={{ flex: 1, marginLeft: 10 }}>
          <Input
            format='currency'
            invalid={errors.includes('face_amount_end')}
            onChange={handleInputChange.bind(null, 'face_amount_end')}
            required={true}
            value={data.face_amount_end}
          />
        </Field>
      </div>

      <div style={{ display: 'flex' }}>
        <Field label='Age Range Start' style={{ flex: 1 }}>
          <Input
            invalid={errors.includes('age_start')}
            onChange={handleInputChange.bind(null, 'age_start')}
            required={true}
            type='number'
            value={data.age_start}
          />
        </Field>
        <Field label='Age Range End' style={{ flex: 1, marginLeft: 10 }}>
          <Input
            invalid={errors.includes('age_end')}
            onChange={handleInputChange.bind(null, 'age_end')}
            required={true}
            type='number'
            value={data.age_end}
          />
        </Field>
      </div>

      <FieldGroup contained={true} label='Premium Limitations'>
        <div style={{ display: 'flex' }}>
          <Field label='Annual Min' style={{ flex: 1 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'min_premium_annual')}
              value={data.min_premium_annual}
            />
          </Field>
          <Field label='Annual Max' style={{ flex: 1, marginLeft: 10 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'max_premium_annual')}
              value={data.max_premium_annual}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Semi-Annual Min' style={{ flex: 1 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'min_premium_semi_annual')}
              value={data.min_premium_semi_annual}
            />
          </Field>
          <Field label='Semi-Annual Max' style={{ flex: 1, marginLeft: 10 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'max_premium_semi_annual')}
              value={data.max_premium_semi_annual}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Quarterly Min' style={{ flex: 1 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'min_premium_quarterly')}
              value={data.min_premium_quarterly}
            />
          </Field>
          <Field label='Quarterly Max' style={{ flex: 1, marginLeft: 10 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'max_premium_quarterly')}
              value={data.max_premium_quarterly}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Monthly Min' style={{ flex: 1 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'min_premium_monthly')}
              value={data.min_premium_monthly}
            />
          </Field>
          <Field label='Monthly Max' style={{ flex: 1, marginLeft: 10 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'max_premium_monthly')}
              value={data.max_premium_monthly}
            />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup contained={true} label='Policy Fees'>
        <div style={{ display: 'flex' }}>
          <Field label='Annual' style={{ flex: 1 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'policy_fee_annual')}
              value={data.policy_fee_annual}
            />
          </Field>
          <Field label='Semi-Annual' style={{ flex: 1, marginLeft: 10 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'policy_fee_semi_annual')}
              value={data.policy_fee_semi_annual}
            />
          </Field>
        </div>

        <div style={{ display: 'flex' }}>
          <Field label='Quarterly' style={{ flex: 1 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'policy_fee_quarterly')}
              value={data.policy_fee_quarterly}
            />
          </Field>
          <Field label='Monthly' style={{ flex: 1, marginLeft: 10 }}>
            <Input
              format='currency_decimal'
              onChange={handleInputChange.bind(null, 'policy_fee_monthly')}
              value={data.policy_fee_monthly}
            />
          </Field>
        </div>
      </FieldGroup>

      <Field>
        <Checkbox
          checked={data.disable_flat_extra}
          label='Disable Flat Extra'
          onChange={handleCheckboxChange.bind(null, 'disable_flat_extra')}
        />
      </Field>
    </Drawer>
  );
};

Edit.propTypes = {
  band: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  show: PropTypes.bool
};

export default Edit;
